import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GameSave, IPuzzle } from '../../model/IPuzzle';
import { RootState } from '..';
import { getPuzzleIndexForDate } from '../../utils/Puzzle';
import { setOpenModal } from './Navigation';
import { v4 as uuidv4 } from 'uuid';
import AES from 'crypto-js/aes';
import { createDateStamp } from '../../utils';
import { getCryptoKey, getApiUrl } from '../../utils/config';
import { player } from '../../main';
import { createElement } from 'react';
import { Notify, NotifyComponent } from '../../utils/Notification';
import CountsUnlockedNotification from '../../components/CountsUnlockedNotification';
import enc from 'crypto-js/enc-utf8';

export type MessageBubble = {
  type: 'good' | 'bad' | 'bonus';
  message: string;
};

export type Direction =
  | 'north'
  | 'north-east'
  | 'east'
  | 'south-east'
  | 'south'
  | 'south-west'
  | 'west'
  | 'north-west';

export type DrawTrack = {
  x: number;
  y: number;
  pX: number;
  pY: number;
  label: string;
  direction?: Direction;
  isFinish?: boolean;
};

export interface GameState {
  isNewPlayer: boolean;
  userId: string;
  archive: GameSave[];
  messageBubble: MessageBubble | null;
  currentGameIdx: number;
  lastDailyWin?: string;
  currentStreak: number;
  bestStreak: number;
  lastDailyWin3x3?: string;
  currentStreak3x3: number;
  bestStreak3x3: number;
  puzzlesLoading: boolean;
  isReviewAsked: boolean;
  drawTrack: DrawTrack[];
  correctTiles: { x: number; y: number; idx: number }[];
  isSubmiting: boolean;
  groupBy: 'order' | 'length';
  isTutorialCompleted: boolean;
  todaysPuzzleMissing: boolean;
}

const initialState: GameState = {
  isNewPlayer: true,
  userId: uuidv4(),
  archive: [],
  messageBubble: null,
  currentGameIdx: 0,
  currentStreak: 0,
  bestStreak: 0,
  currentStreak3x3: 0,
  bestStreak3x3: 0,
  puzzlesLoading: false,
  isReviewAsked: false,
  drawTrack: [],
  correctTiles: [],
  isSubmiting: false,
  groupBy: 'length',
  isTutorialCompleted: false,
  todaysPuzzleMissing: false,
};

const decideBubble = (n: number) => {
  if (n === 4) {
    return `Good! +${n}`;
  }
  if (n > 4 && n < 7) {
    return `Great! +${n}`;
  }
  if (n >= 7) {
    return `Amazing! +${n}`;
  }
  return `Good! +${n}`;
};

export interface GameError {
  type: 'too_short' | 'no_word' | 'found';
  message: string;
}

export const solveOne = createAsyncThunk(
  'game/solveOne',
  async (_, { getState, dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      dispatch(setIsNewPlayer(false));
      const state = getState() as RootState;
      const cellMode3x3 = state.options.cellMode3x3 ?? false;
      const gameIdx = state.game.currentGameIdx;
      const game = state.game.archive[gameIdx];
      const words = game.words;
      const found = game.foundWords;

      const left = words.slice().filter((e) => !found.includes(e));

      if (left.length > 0) {
        const random = left[Math.floor(Math.random() * left.length)];
        dispatch(addToFound({ word: random, is3x3: cellMode3x3 }));
      }

      if (left.length - 1 === 0) {
        const currentStreak = cellMode3x3 ? state.game.currentStreak3x3 : state.game.currentStreak;
        const bestStreak = cellMode3x3 ? state.game.bestStreak3x3 : state.game.bestStreak;

        dispatch(updateLastDailyPlay({ date: createDateStamp(), isMini: cellMode3x3 }));
        dispatch(setBestStreak({ value: Math.max(currentStreak + 1, bestStreak), isMini: cellMode3x3 }));
        dispatch(setCurrentStreak({ value: currentStreak + 1, isMini: cellMode3x3 }));
        setTimeout(() => dispatch(setOpenModal('game-end')), 3000);
      }

      return fulfillWithValue('success');
    } catch (err) {
      const error: Error = err as Error;
      return rejectWithValue(error.message);
    }
  },
);

export const instaWin = createAsyncThunk(
  'game/justWin',
  async (_, { getState, dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      dispatch(setIsNewPlayer(false));
      const state = getState() as RootState;
      const cellMode3x3 = state.options.cellMode3x3 ?? false;
      const gameIdx = state.game.currentGameIdx;
      const game = state.game.archive[gameIdx];
      const words = game.words;
      const found = game.foundWords;

      const left = words.slice().filter((e) => !found.includes(e));

      left.forEach((e) => {
        dispatch(addToFound({ word: e, is3x3: cellMode3x3 }));
      });

      const currentStreak = cellMode3x3 ? state.game.currentStreak3x3 : state.game.currentStreak;
      const bestStreak = cellMode3x3 ? state.game.bestStreak3x3 : state.game.bestStreak;

      dispatch(updateLastDailyPlay({ date: createDateStamp(), isMini: cellMode3x3 }));
      dispatch(setBestStreak({ value: Math.max(currentStreak + 1, bestStreak), isMini: cellMode3x3 }));
      dispatch(setCurrentStreak({ value: currentStreak + 1, isMini: cellMode3x3 }));
      setTimeout(() => dispatch(setOpenModal('game-end')), 3000);
      return fulfillWithValue('success');
    } catch (err) {
      const error: Error = err as Error;
      return rejectWithValue(error.message);
    }
  },
);

export const submit = createAsyncThunk(
  'game/submit',
  async (
    stuff: { good: () => Promise<void>; bad: () => Promise<void> },
    { getState, dispatch, fulfillWithValue, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const cellMode3x3 = state.options.cellMode3x3 ?? false;
      const stack = state.game.drawTrack;
      const input = stack.reduce((e, i) => e + i.label, '');

      const disableSound = state.options.disableSound;
      const animationsDisabled = state.options.disableAnimation;

      const gameIdx = state.game.currentGameIdx;
      const game = state.game.archive[gameIdx];

      const letterStartTriggered = game.letterStartTriggered;
      const letterTotalTriggered = game.letterTotalTriggered;

      const words = game.words;
      const found = game.foundWords;
      const bonusWords = game.bonusWords;
      const foundBonusWords = game.foundBonusWords;
      const wordOfDay = game.x5;

      const isPrimary = words.includes(input);
      const isBonus = bonusWords.includes(input);
      const isWordOfDay = wordOfDay === input;

      let err: GameError | null = null;

      if (!isPrimary && !isBonus) {
        err = { type: 'no_word', message: 'Word not found' };
      }

      if (input.length < (cellMode3x3 ? 3 : 4)) {
        err = { type: 'too_short', message: 'Too short' };
      }

      if (found.includes(input) || foundBonusWords.includes(input)) {
        err = { type: 'found', message: 'Already found' };
      }

      if (!err) {
        if (isPrimary) {
          //win
          const isWin = found.length + 1 === words.length;
          if (isWin) {
            const currentStreak = cellMode3x3 ? state.game.currentStreak3x3 : state.game.currentStreak;
            const bestStreak = cellMode3x3 ? state.game.bestStreak3x3 : state.game.bestStreak;

            dispatch(updateLastDailyPlay({ date: createDateStamp(), isMini: cellMode3x3 }));
            dispatch(setBestStreak({ value: Math.max(currentStreak + 1, bestStreak), isMini: cellMode3x3 }));
            dispatch(setCurrentStreak({ value: currentStreak + 1, isMini: cellMode3x3 }));
            setTimeout(() => dispatch(setOpenModal('game-end')), 3000);
          }

          const a = document.querySelector('html');
          a?.style.setProperty('--line-current', 'var(--line-correct)');
          a?.style.setProperty('--letter-current', 'var(--letter-correct)');
          a?.style.setProperty(
            '--letter-label-current',
            'var(--letter-label-bright)',
          );
          a?.style.setProperty(
            '--letter-border-current',
            'var(--letter-correct-border)',
          );
          if (!disableSound) {
            if (isWin) player.soundWin();
            else player.soundCorrect();
          }
          if (!animationsDisabled) {
            dispatch(setCorrectTiles(stack.map((t, i) => ({ x: t.pX, y: t.pY, idx: i }))));
          }
          await stuff.good();
          dispatch(addToFound({ word: input, is3x3: cellMode3x3 }));
          dispatch(
            setBubble({ type: 'good', message: decideBubble(input.length) }),
          );

          if (!letterStartTriggered) {
            const max = words.length;
            const currentScore = found.length;
            const shouldShowStart = Math.floor((currentScore / max) * 100) > 40;
            if (shouldShowStart) {
              NotifyComponent(createElement(CountsUnlockedNotification));
              dispatch(triggerShowStart());
            }
          }

        }

        if (isBonus) {
          if (isWordOfDay) {
            Notify(
              'Found word of the day',
              'You have found bonus word of the day',
            );
            dispatch(refillHintsLeft(2));
          }
          const a = document.querySelector('html');
          a?.style.setProperty('--line-current', 'var(--line-bonus)');
          a?.style.setProperty('--letter-current', 'var(--letter-bonus)');
          a?.style.setProperty(
            '--letter-label-current',
            'var(--letter-label-bright)',
          );
          a?.style.setProperty(
            '--letter-border-current',
            'var(--letter-bonus-border)',
          );
          if (!disableSound) player.soundBonus();
          if (!animationsDisabled) {
            dispatch(setCorrectTiles(stack.map((t, i) => ({ x: t.pX, y: t.pY, idx: i }))));
          }
          await stuff.good();
          dispatch(addToBonusFound({ word: input, is3x3: cellMode3x3 }));
          dispatch(setBubble({ type: 'bonus', message: 'Bonus word' }));
        }
      }

      if (err) {
        if (!disableSound) player.soundFail();
        const a = document.querySelector('html');
        a?.style.setProperty('--line-current', 'var(--line-wrong)');
        a?.style.setProperty('--letter-current', 'var(--letter-wrong)');
        a?.style.setProperty(
          '--letter-border-current',
          'var(--letter-wrong-border)',
        );
        await stuff.bad();
        dispatch(setBubble({ type: 'bad', message: err.message }));
      }

      if (animationsDisabled) {
        setTimeout(() => {
          const a = document.querySelector('html');
          a?.style.setProperty('--line-current', 'var(--line-selected)');
          a?.style.setProperty('--letter-current', 'var(--letter-selected)');
          a?.style.setProperty(
            '--letter-label-current',
            'var(--letter-label-dark)',
          );
          a?.style.setProperty(
            '--letter-border-current',
            'var(--letter-selected-border)',
          );
          dispatch(resetDrawTrack());
        }, 350);
      } else {
        const a = document.querySelector('html');
        a?.style.setProperty('--line-current', 'var(--line-selected)');
        a?.style.setProperty('--letter-current', 'var(--letter-selected)');
        a?.style.setProperty(
          '--letter-label-current',
          'var(--letter-label-dark)',
        );
        a?.style.setProperty(
          '--letter-border-current',
          'var(--letter-selected-border)',
        );
        dispatch(resetDrawTrack());
      }

      // Clear tile pop animation after it completes
      setTimeout(() => {
        dispatch(clearCorrectTiles());
      }, 900);

      dispatch(setIsNewPlayer(false));
      return fulfillWithValue('success');
    } catch (err) {
      const error: Error = err as Error;
      return rejectWithValue(error.message);
    }
  },
);

export const sendReport = createAsyncThunk(
  'game/report',
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const archive = (getState() as RootState).game.archive;
      const userId = (getState() as RootState).game.userId;
      const POST_URL = getApiUrl() + 'v1/report/send-report';
      const response = await fetch(POST_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          report: AES.encrypt(
            JSON.stringify(archive),
            getCryptoKey(),
          ).toString(),
        }),
      });
      if (!response.ok) throw new Error('Error while sending report');
      return fulfillWithValue('success');
    } catch (err) {
      const error: Error = err as Error;
      return rejectWithValue(error.message);
    }
  },
);

export const sendFeedback = createAsyncThunk(
  'game/feedback',
  async (message: string, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const userId = (getState() as RootState).game.userId;
      const POST_URL = getApiUrl() + 'v1/mail/send-feedback';
      const response = await fetch(POST_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          message: message,
        }),
      });
      if (!response.ok) throw new Error('Error while sending report');
      return fulfillWithValue('success');
    } catch (err) {
      const error: Error = err as Error;
      return rejectWithValue(error.message);
    }
  },
);

export const loadPuzzles = createAsyncThunk(
  'game/loadPuzzles',
  async (cellMode3x3: boolean, { rejectWithValue }) => {
    let puzzles: IPuzzle[] | null = null;

    // Try API first
    try {
      const GET_URL =
        getApiUrl() +
        `v1/puzzle/daily?offset=${getPuzzleIndexForDate(new Date())}&cell=${
          cellMode3x3 ? '3' : '4'
        }`;
      const response = await fetch(GET_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error('Error while loading puzzles from API');
      const bytes = AES.decrypt(data.puzzles, getCryptoKey());
      const originalText = bytes.toString(enc);
      puzzles = JSON.parse(originalText);
    } catch (err) {
      const error: Error = err as Error;
      return rejectWithValue({ message: error.message, is3x3: cellMode3x3 });
    }

    return { puzzles: puzzles!, is3x3: cellMode3x3 };
  },
);

export const loadArchivePuzzle = createAsyncThunk(
  'game/loadArchivePuzzle',
  async (
    { puzzleIdx, cellMode3x3 }: { puzzleIdx: number; cellMode3x3: boolean },
    { rejectWithValue },
  ) => {
    // Always fetch from API
    const offset = puzzleIdx - 1;
    try {
      const GET_URL =
        getApiUrl() +
        `v1/puzzle/daily?offset=${offset}&cell=${cellMode3x3 ? '3' : '4'}`;
      const response = await fetch(GET_URL, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) throw new Error('Error while loading archive puzzle');
      const bytes = AES.decrypt(data.puzzles, getCryptoKey());
      const originalText = bytes.toString(enc);
      const puzzles: IPuzzle[] = JSON.parse(originalText);
      return { puzzles, is3x3: cellMode3x3, targetIdx: puzzleIdx };
    } catch (err) {
      const error: Error = err as Error;
      return rejectWithValue({ message: error.message, is3x3: cellMode3x3 });
    }
  },
);

export const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    toggleReviewAsked: (state) => {
      state.isReviewAsked = true;
    },
    setIsNewPlayer: (state, action: PayloadAction<boolean>) => {
      state.isNewPlayer = action.payload;
    },
    setBestStreak: (state, action: PayloadAction<{ value: number; isMini: boolean }>) => {
      if (action.payload.isMini) {
        state.bestStreak3x3 = action.payload.value;
      } else {
        state.bestStreak = action.payload.value;
      }
    },
    setCurrentStreak: (state, action: PayloadAction<{ value: number; isMini: boolean }>) => {
      if (action.payload.isMini) {
        state.currentStreak3x3 = action.payload.value;
      } else {
        state.currentStreak = action.payload.value;
      }
    },
    updateLastDailyPlay: (state, action: PayloadAction<{ date: string; isMini: boolean }>) => {
      if (action.payload.isMini) {
        state.lastDailyWin3x3 = action.payload.date;
      } else {
        state.lastDailyWin = action.payload.date;
      }
    },
    setCurrentGame: (state, action) => {
      if (state.archive.length >= action.payload) {
        state.currentGameIdx = action.payload;
      }
    },
    setBubble: (state, action: PayloadAction<MessageBubble | null>) => {
      state.messageBubble = action.payload;
    },
    pushDrawTrack: (state, action: PayloadAction<DrawTrack>) => {
      state.drawTrack = [...state.drawTrack, action.payload];
    },
    popDrawTrack: (state) => {
      state.drawTrack = state.drawTrack.slice(0, -1);
    },
    resetDrawTrack: (state) => {
      state.drawTrack = [];
    },
    setCorrectTiles: (state, action: PayloadAction<{ x: number; y: number; idx: number }[]>) => {
      state.correctTiles = action.payload;
    },
    clearCorrectTiles: (state) => {
      state.correctTiles = [];
    },
    addToFound: (
      state,
      action: PayloadAction<{ word: string; is3x3: boolean }>,
    ) => {
      const game = state.archive[state.currentGameIdx];
      game.foundWords = [...game.foundWords, action.payload.word];
    },
    addToBonusFound: (
      state,
      action: PayloadAction<{ word: string; is3x3: boolean }>,
    ) => {
      const game = state.archive[state.currentGameIdx];
      game.foundBonusWords = [...game.foundBonusWords, action.payload.word];
    },
    triggerShowTotal: (state) => {
      state.archive[state.currentGameIdx].letterTotalTriggered = true;
    },
    triggerShowStart: (state) => {
      state.archive[state.currentGameIdx].letterStartTriggered = true;
    },
    consumeHint: (state) => {
      state.archive[state.currentGameIdx].hintsLeft =
        state.archive[state.currentGameIdx].hintsLeft - 1;
    },
    refillHintsLeft: (state, action: PayloadAction<number>) => {
      state.archive[state.currentGameIdx].hintsLeft =
        state.archive[state.currentGameIdx].hintsLeft + action.payload;
    },
    resetIsSubmitting: (state) => {
      state.isSubmiting = false;
    },
    setGroupBy: (state, action: PayloadAction<'order' | 'length'>) => {
      state.groupBy = action.payload;
    },
    triggerShare: (state) => {
      state.archive[state.currentGameIdx].shareUsed = true;
    },
    completeTutorial: (state) => {
      state.isTutorialCompleted = true;
      state.isNewPlayer = false;
    },
    resetPuzzle: (state, action: PayloadAction<boolean | undefined>) => {
      const is3x3 = action.payload ?? false;
      const game = state.archive[state.currentGameIdx];
      game.foundBonusWords = [];
      game.foundWords = [];
      game.shareUsed = false;
      game.hintsLeft = 3;
    },
    resetStats: (state, action: PayloadAction<boolean>) => {
      const is3x3 = action.payload;
      const gridSize = is3x3 ? 3 : 4;
      // Clear found words for all games of the given grid size (except today's)
      state.archive.forEach((g, i) => {
        const size = g.gridSize ?? g.board.length;
        if (size === gridSize && i !== state.currentGameIdx) {
          g.foundWords = [];
          g.foundBonusWords = [];
          g.totalWords = undefined;
        }
      });
      // Reset streaks
      if (is3x3) {
        state.currentStreak3x3 = 0;
        state.bestStreak3x3 = 0;
      } else {
        state.currentStreak = 0;
        state.bestStreak = 0;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadPuzzles.pending, (state) => {
      state.puzzlesLoading = true;
    });
    builder.addCase(loadPuzzles.rejected, (state, action) => {
      state.puzzlesLoading = false;

      const payload = action.payload as { is3x3: boolean; message: string } | undefined;
      const is3x3 = payload?.is3x3 ?? false;

      const todayIdx = getPuzzleIndexForDate(new Date()) + 1;
      const gridSize = is3x3 ? 3 : 4;
      const todayGame = state.archive
        .filter((g) => g.idx === todayIdx && (g.board.length === gridSize || g.gridSize === gridSize))
        .slice(-1)[0];

      if (todayGame) {
        state.currentGameIdx = state.archive.indexOf(todayGame);
        state.todaysPuzzleMissing = false;
      } else {
        state.todaysPuzzleMissing = true;
      }
    });
    builder.addCase(
      loadPuzzles.fulfilled,
      (
        state,
        action: PayloadAction<{ puzzles: IPuzzle[]; is3x3: boolean }>,
      ) => {
        const { puzzles, is3x3 } = action.payload;
        const copy = state.archive.slice();

        puzzles.forEach((e) => {
          const id = e.board.join('-');
          const parsedBoard = e.board.map((row, y) =>
            row.split('').map((c, x) => ({ x, y, label: c })),
          );
          const parsedWords = e.words;
          const parsedBonus = e.optionalWords;
          const parsedDefs = e.definitions || [];
          const parsedX5 = Array.isArray(e.x5) ? e.x5[0] : (e.x5 || '');
          const gridSize = parsedBoard.length;

          // Match by board-hash first, then fall back to puzzle index + grid size
          const existing =
            copy.find((g) => g.id === id) ||
            copy.find(
              (g) =>
                g.idx === e.id &&
                (g.gridSize === gridSize || g.board.length === gridSize),
            );

          if (existing) {
            // Restore puzzle data stripped by migration 3 / gameTransform
            existing.id = id;
            existing.board = parsedBoard;
            existing.words = parsedWords;
            existing.bonusWords = parsedBonus;
            existing.definitions = parsedDefs;
            existing.x5 = parsedX5;
            existing.idx = e.id;
            existing.gridSize = gridSize;
            existing.totalWords = parsedWords.length;
          } else {
            copy.push({
              id,
              board: parsedBoard,
              words: parsedWords,
              idx: e.id,
              bonusWords: parsedBonus,
              definitions: parsedDefs,
              x5: parsedX5,
              foundBonusWords: [],
              foundWords: [],
              hintsLeft: 3,
              letterStartTriggered: false,
              letterTotalTriggered: false,
              shareUsed: false,
              gridSize,
              totalWords: parsedWords.length,
            });
          }
        });

        // Back-fill totalWords for any archive games the API didn't return.
        // foundWords.length is the best data we have – the player found at
        // least that many words, and for completed games it equals the total.
        copy.forEach((g) => {
          if (g.totalWords == null && g.foundWords.length > 0) {
            g.totalWords = g.foundWords.length;
          }
        });

        state.archive = copy;
        state.puzzlesLoading = false;

        const todayIdx = getPuzzleIndexForDate(new Date()) + 1;
        const gridSize = is3x3 ? 3 : 4;

        /**
         * If the board was updated, there may be multiple games
         * with today's index in the archive. We take the last one.
         * The same logic applies in the rejected case.
         */
        const todayGame = copy.filter(
          (g) => g.idx === todayIdx && (g.board.length === gridSize || g.gridSize === gridSize),
        ).slice(-1)[0];

        if (todayGame) {
          state.currentGameIdx = copy.indexOf(todayGame);
          state.todaysPuzzleMissing = false;
        } else {
          state.todaysPuzzleMissing = true;
        }
      },
    );
    builder.addCase(submit.pending, (state) => {
      state.isSubmiting = true;
    });
    builder.addCase(submit.rejected, (state) => {
      state.isSubmiting = false;
    });
    builder.addCase(submit.fulfilled, (state) => {
      state.isSubmiting = false;
    });
    builder.addCase(loadArchivePuzzle.pending, () => {
      // Do NOT set puzzlesLoading — it gates the entire app in App.tsx
      // and would unmount the router, preventing navigation after load.
    });
    builder.addCase(loadArchivePuzzle.rejected, () => {
      // puzzlesLoading was never set, nothing to reset.
    });
    builder.addCase(
      loadArchivePuzzle.fulfilled,
      (state, action: PayloadAction<{ puzzles: IPuzzle[]; is3x3: boolean; targetIdx: number }>) => {
        const { puzzles, is3x3, targetIdx } = action.payload;
        const copy = state.archive.slice();
        const gridSize = is3x3 ? 3 : 4;

        // Track the index of the entry matching targetIdx so we can set it as current
        let targetCopyIdx = -1;

        puzzles.forEach((e) => {
          const id = e.board.join('-');
          const parsedBoard = e.board.map((row, y) =>
            row.split('').map((c, x) => ({ x, y, label: c })),
          );
          const parsedWords = e.words;
          const parsedBonus = e.optionalWords;
          const parsedDefs = e.definitions || [];
          const parsedX5 = Array.isArray(e.x5) ? e.x5[0] : (e.x5 || '');
          const gs = parsedBoard.length;

          const existing =
            copy.find((g) => g.id === id) ||
            copy.find((g) => g.idx === e.id && (g.gridSize === gs || g.board.length === gs));

          if (existing) {
            existing.id = id;
            existing.board = parsedBoard;
            existing.words = parsedWords;
            existing.bonusWords = parsedBonus;
            existing.definitions = parsedDefs;
            existing.x5 = parsedX5;
            existing.idx = e.id;
            existing.gridSize = gs;
            existing.totalWords = parsedWords.length;
            // Track if this is the puzzle the user requested
            if (e.id === targetIdx) {
              targetCopyIdx = copy.indexOf(existing);
            }
          } else {
            const newEntry = {
              id,
              board: parsedBoard,
              words: parsedWords,
              idx: e.id,
              bonusWords: parsedBonus,
              definitions: parsedDefs,
              x5: parsedX5,
              foundBonusWords: [] as string[],
              foundWords: [] as string[],
              hintsLeft: 3,
              letterStartTriggered: false,
              letterTotalTriggered: false,
              shareUsed: false,
              gridSize: gs,
              totalWords: parsedWords.length,
            };
            copy.push(newEntry);
            if (e.id === targetIdx) {
              targetCopyIdx = copy.length - 1;
            }
          }
        });

        state.archive = copy;

        // Set current game — use the tracked index, or fall back to searching
        if (targetCopyIdx >= 0) {
          state.currentGameIdx = targetCopyIdx;
        } else {
          // API didn't return a puzzle matching targetIdx — find the first
          // puzzle it DID return (with actual data) as a fallback
          const fallback = puzzles.length > 0
            ? copy.findIndex(
                (g) => g.board.length > 0 && g.words.length > 0 &&
                  (g.gridSize === gridSize || g.board.length === gridSize) &&
                  g.idx === puzzles[0].id,
              )
            : -1;
          if (fallback >= 0) {
            state.currentGameIdx = fallback;
          }
        }
      },
    );
  },
});

// Action creators are generated for each case reducer function
export const {
  resetPuzzle,
  completeTutorial,
  triggerShare,
  refillHintsLeft,
  setGroupBy,
  resetIsSubmitting,
  consumeHint,
  addToBonusFound,
  triggerShowStart,
  triggerShowTotal,
  addToFound,
  pushDrawTrack,
  popDrawTrack,
  resetDrawTrack,
  toggleReviewAsked,
  setIsNewPlayer,
  setBestStreak,
  setCurrentStreak,
  updateLastDailyPlay,
  setCurrentGame,
  setBubble,
  setCorrectTiles,
  clearCorrectTiles,
  resetStats,
} = gameSlice.actions;

export default gameSlice.reducer;

export const GameBoard = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return [];
  return state.game.archive[state.game.currentGameIdx].board;
};

export const GameFoundWords = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return [];
  const game = state.game.archive[state.game.currentGameIdx];
  return game.foundWords;
};

export const GameAllWords = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return [];
  return state.game.archive[state.game.currentGameIdx]?.words;
};

export const GameFoundBonus = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return [];
  const game = state.game.archive[state.game.currentGameIdx];
  return game.foundBonusWords;
};

export const GameAllBonus = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return [];
  return state.game.archive[state.game.currentGameIdx].bonusWords;
};

export const CurrentGameIDX_REPRESENT = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return 0;
  return state.game.archive[state.game.currentGameIdx].idx;
};

export const TodayIndex = (state: RootState) => {
  const DayIdx = getPuzzleIndexForDate(new Date());
  const id = Math.min(state.game.archive.length - 1, DayIdx);
  return id;
};

export const GameIsOver = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return false;
  const game = state.game.archive[state.game.currentGameIdx];
  if (!game.words || game.words.length === 0) return false;
  return game.foundWords.length === game.words.length;
};

export const ShowTotalLetter = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return false;
  return state.game.archive[state.game.currentGameIdx].letterTotalTriggered;
};

export const showStartLetter = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return false;
  return state.game.archive[state.game.currentGameIdx].letterStartTriggered;
};

export const HintsLeft = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return 0;
  return state.game.archive[state.game.currentGameIdx].hintsLeft;
};

export const DailyWord = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return '';
  return state.game.archive[state.game.currentGameIdx].x5;
};

export const ShareUsed = (state: RootState) => {
  if (state.game.archive.length <= state.game.currentGameIdx) return false;
  return state.game.archive[state.game.currentGameIdx].shareUsed;
};
